import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import {
  adminGetAgents,
  adminGetAgent,
  adminCreateAgent,
  adminUpdateAgent,
  adminDeleteAgent,
  adminAssignAgentSubscription,
  adminCreateAgentStripeBillingPortalSession,
  adminGetCustomers,
  adminGetPlans,
  getApiError,
  pickStripeBillingPortalUrl,
} from '../../lib/api';
import { getPublicAppBaseUrl } from '../../lib/apiBaseUrl';
import { formatDateTime } from '../../lib/utils';
import {
  getPurchasedMessageCount,
  getRemainingMessageCount,
  getUsedMessageCount,
} from '../../lib/subscriptionQuota';
import { toast } from 'sonner';
import {
  Bot,
  Plus,
  ChevronRight,
  ChevronLeft,
  ArrowLeft,
  ArrowRight,
  Phone,
  Building2,
  Clock,
  Calendar,
  Edit,
  Trash2,
  Loader2,
  Search,
  Check,
  Settings,
  Mic,
  MessageSquare,
  PhoneCall,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';

const INDUSTRIES = [
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'construction', label: 'Construction' },
  { value: 'medical', label: 'Medical' },
  { value: 'legal', label: 'Legal' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'retail', label: 'Retail' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'other', label: 'Other' },
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DEFAULT_BUSINESS_HOURS = {
  monday: { open: '09:00', close: '17:00', enabled: true },
  tuesday: { open: '09:00', close: '17:00', enabled: true },
  wednesday: { open: '09:00', close: '17:00', enabled: true },
  thursday: { open: '09:00', close: '17:00', enabled: true },
  friday: { open: '09:00', close: '17:00', enabled: true },
  saturday: { open: '09:00', close: '13:00', enabled: false },
  sunday: { open: '09:00', close: '13:00', enabled: false },
};

export function AdminAgentsPage() {
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customer');
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const limit = 20;

  useEffect(() => {
    loadAgents();
  }, [page, customerId]);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const params = { limit, skip: page * limit };
      if (customerId) params.customer_id = customerId;
      const response = await adminGetAgents(params);
      setAgents(response.data.agents || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const filteredAgents = agents.filter(a =>
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.customer?.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-agents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-jakarta font-bold text-slate-900">AI Agents</h1>
          <p className="text-slate-500">Manage voice agents across all customers</p>
        </div>
        <Button asChild data-testid="add-agent-btn">
          <Link to={`/admin/agents/new${customerId ? `?customer=${customerId}` : ''}`}>
            <Plus className="w-4 h-4 mr-2" />
            Create Agent
          </Link>
        </Button>
      </div>

      <Card className="border-0 shadow-md">
        <CardHeader className="pb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search agents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="agent-search"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="w-10 h-10 rounded-lg" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredAgents.length === 0 ? (
            <div className="text-center py-16">
              <Bot className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">No agents found</h3>
              <p className="text-slate-500 mb-4">Create your first AI agent</p>
              <Button asChild>
                <Link to="/admin/agents/new">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Agent
                </Link>
              </Button>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead>Subscription</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAgents.map((agent) => (
                    <TableRow key={agent.agent_id} data-testid={`agent-row-${agent.agent_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Bot className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{agent.name}</p>
                            <p className="text-xs text-slate-400">Agent ID: {agent.agent_id}</p>
                            {agent.phone_number && (
                              <p className="text-sm text-slate-500">{agent.phone_number}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {agent.subscription ? (
                          <div className="space-y-1">
                            <Badge>{agent.subscription.plan_name}</Badge>
                            <p className="text-xs text-slate-500">
                              Purchased: {getPurchasedMessageCount(agent)} messages
                            </p>
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-slate-500">
                                Left: {getRemainingMessageCount(agent) ?? 0} messages
                              </p>
                              {agent.status === 'active' && (getRemainingMessageCount(agent) ?? 0) < 10 && (
                                <Badge variant="destructive" className="text-[10px] px-2 py-0.5">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Low quota
                                </Badge>
                              )}
                            </div>
                          </div>
                        ) : (
                          <Badge variant="secondary">No Plan</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>
                          {agent.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/admin/agents/${agent.agent_id}`}>
                            View
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-slate-100">
                  <p className="text-sm text-slate-500">
                    Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p - 1)}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600 px-2">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= totalPages - 1}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function AdminAgentCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCustomer = searchParams.get('customer');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    customer_id: preselectedCustomer || '',
    name: '',
    description: '',
    phone_number: '',
    industry: '',
    // Step 2: Business Hours
    business_hours: DEFAULT_BUSINESS_HOURS,
    timezone: 'Australia/Sydney',
    additional_details: '',
    // Step 3: Voice Settings (Optional)
    greeting_message: '',
    voice_provider: 'elevenlabs',
    voice_id: '',
    // Step 4: Prompts (Optional)
    system_prompt: '',
    first_message: '',
    // Step 5: Subscription
    plan_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const customersRes = await adminGetCustomers(100, 0);
      setCustomers(customersRes.data.customers || []);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await adminCreateAgent(formData);
      toast.success('Agent created successfully');
      const createdAgentId = response?.data?.agent_id || response?.data?.agent?.agent_id;
      if (!createdAgentId) {
        throw new Error('Agent ID not returned');
      }
      navigate(`/admin/agents/${createdAgentId}/configure?fromCreate=1`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.customer_id && formData.name && formData.industry;
      case 2:
        return true;
      case 3:
        return true;
      case 4:
        return true;
      case 5:
        return true;
      default:
        return false;
    }
  };

  const steps = [
    { num: 1, title: 'Basic Info', icon: Bot },
    { num: 2, title: 'Business Hours', icon: Clock },
    { num: 3, title: 'Voice Settings', icon: Mic },
    { num: 4, title: 'Prompts', icon: MessageSquare },
    { num: 5, title: 'Subscription', icon: Settings },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-agent-create">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/agents')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-jakarta font-bold text-slate-900">Create AI Agent</h1>
        <p className="text-slate-500">Set up a new voice agent for your customer</p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-between max-w-3xl">
        {steps.map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <div className={`flex items-center gap-2 ${step >= s.num ? 'text-blue-600' : 'text-slate-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                step > s.num ? 'bg-blue-600 border-blue-600 text-white' :
                step === s.num ? 'border-blue-600 text-blue-600' : 'border-slate-300'
              }`}>
                {step > s.num ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <span className="hidden sm:block text-sm font-medium">{s.title}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`w-12 sm:w-20 h-0.5 mx-2 ${step > s.num ? 'bg-blue-600' : 'bg-slate-200'}`} />
            )}
          </div>
        ))}
      </div>

      <Card className="max-w-3xl">
        <CardContent className="p-6">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label>Customer *</Label>
                    <Select
                      value={formData.customer_id}
                      onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                    >
                      <SelectTrigger data-testid="customer-select">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.user_id} value={c.user_id}>
                            {c.company_name || c.name} ({c.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Agent Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Appointment Booking Agent"
                        data-testid="agent-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone Number</Label>
                      <Input
                        value={formData.phone_number}
                        onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                        placeholder="+61..."
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Industry Type *</Label>
                    <Select
                      value={formData.industry}
                      onValueChange={(value) => setFormData({ ...formData, industry: value })}
                    >
                      <SelectTrigger data-testid="industry-select">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDUSTRIES.map((ind) => (
                          <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description of what this agent does..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Business Hours */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-4">Business Hours</h2>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <Select
                      value={formData.timezone}
                      onValueChange={(value) => setFormData({ ...formData, timezone: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Australia/Sydney">Australia/Sydney (AEST)</SelectItem>
                        <SelectItem value="Australia/Melbourne">Australia/Melbourne (AEST)</SelectItem>
                        <SelectItem value="Australia/Brisbane">Australia/Brisbane (AEST)</SelectItem>
                        <SelectItem value="Australia/Perth">Australia/Perth (AWST)</SelectItem>
                        <SelectItem value="Australia/Adelaide">Australia/Adelaide (ACST)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    {DAYS.map((day) => (
                      <div key={day} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                        <div className="w-24">
                          <span className="font-medium capitalize">{day}</span>
                        </div>
                        <Switch
                          checked={formData.business_hours[day]?.enabled}
                          onCheckedChange={(checked) => setFormData({
                            ...formData,
                            business_hours: {
                              ...formData.business_hours,
                              [day]: { ...formData.business_hours[day], enabled: checked }
                            }
                          })}
                        />
                        {formData.business_hours[day]?.enabled && (
                          <>
                            <Input
                              type="time"
                              value={formData.business_hours[day]?.open}
                              onChange={(e) => setFormData({
                                ...formData,
                                business_hours: {
                                  ...formData.business_hours,
                                  [day]: { ...formData.business_hours[day], open: e.target.value }
                                }
                              })}
                              className="w-32"
                            />
                            <span>to</span>
                            <Input
                              type="time"
                              value={formData.business_hours[day]?.close}
                              onChange={(e) => setFormData({
                                ...formData,
                                business_hours: {
                                  ...formData.business_hours,
                                  [day]: { ...formData.business_hours[day], close: e.target.value }
                                }
                              })}
                              className="w-32"
                            />
                          </>
                        )}
                        {!formData.business_hours[day]?.enabled && (
                          <span className="text-slate-500">Closed</span>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2 pt-4">
                    <Label>Additional Details for Building Agent</Label>
                    <Textarea
                      value={formData.additional_details}
                      onChange={(e) => setFormData({ ...formData, additional_details: e.target.value })}
                      placeholder="Provide any specific information about the business, services offered, pricing, booking rules, etc. This will be used to configure the AI agent's knowledge base."
                      rows={6}
                    />
                    <p className="text-sm text-slate-500">
                      Include details like: services offered, pricing, booking rules, special instructions, frequently asked questions, etc.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Voice Settings (Optional) */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-1">Voice Settings</h2>
                <p className="text-slate-500 text-sm mb-4">Optional: Configure voice and greeting settings</p>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label>Greeting Message</Label>
                    <Textarea
                      value={formData.greeting_message}
                      onChange={(e) => setFormData({ ...formData, greeting_message: e.target.value })}
                      placeholder="Hello! Thank you for calling [Business Name]. How can I help you today?"
                      rows={3}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Voice Provider</Label>
                      <Select
                        value={formData.voice_provider}
                        onValueChange={(value) => setFormData({ ...formData, voice_provider: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                          <SelectItem value="azure">Azure</SelectItem>
                          <SelectItem value="google">Google</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Voice ID</Label>
                      <Input
                        value={formData.voice_id}
                        onChange={(e) => setFormData({ ...formData, voice_id: e.target.value })}
                        placeholder="Voice ID from provider"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Prompts (Optional) */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-1">Custom Prompts</h2>
                <p className="text-slate-500 text-sm mb-4">Optional: Define custom prompts for the AI agent</p>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label>System Prompt</Label>
                    <Textarea
                      value={formData.system_prompt}
                      onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                      placeholder="You are a friendly and professional assistant for [Business Name]. Your role is to help customers with..."
                      rows={6}
                    />
                    <p className="text-sm text-slate-500">
                      Define the agent's personality, role, and behavior guidelines.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>First Message</Label>
                    <Textarea
                      value={formData.first_message}
                      onChange={(e) => setFormData({ ...formData, first_message: e.target.value })}
                      placeholder="The first thing the agent says when a call is answered..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Subscription */}
          {step === 5 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-1">Subscription Plan</h2>
                <p className="text-slate-500 text-sm mb-4">Assign a subscription plan to this agent</p>
                <div className="grid gap-4">
                  <div
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      formData.plan_id === ''
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setFormData({ ...formData, plan_id: '' })}
                  >
                    <h3 className="font-semibold text-slate-900">No Plan (Draft)</h3>
                    <p className="text-sm text-slate-500">Create agent without a subscription</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between pt-6 border-t mt-6">
            <Button
              variant="outline"
              onClick={() => setStep(s => s - 1)}
              disabled={step === 1}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            {step < 5 ? (
              <Button
                onClick={() => setStep(s => s + 1)}
                disabled={!canProceed()}
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading} data-testid="create-agent-submit">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Agent
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function AdminAgentDetailPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [billingPortalSendEmail, setBillingPortalSendEmail] = useState(true);

  useEffect(() => {
    loadAgent();
  }, [agentId]);

  const loadAgent = async () => {
    try {
      const response = await adminGetAgent(agentId);
      setAgent(response.data);
    } catch (error) {
      toast.error('Failed to load agent');
      navigate('/admin/agents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await adminDeleteAgent(agentId);
      toast.success('Agent deleted');
      navigate('/admin/agents');
    } catch (error) {
      toast.error('Failed to delete agent');
    }
  };

  const handleStripeBillingPortal = async () => {
    setBillingPortalLoading(true);
    try {
      const base = getPublicAppBaseUrl();
      const returnUrl = `${base}/admin/agents/${encodeURIComponent(agentId)}`;
      const res = await adminCreateAgentStripeBillingPortalSession(agentId, {
        return_url: returnUrl,
        send_email: billingPortalSendEmail,
      });
      const portalUrl = pickStripeBillingPortalUrl(res?.data);
      if (!portalUrl) {
        toast.error('Billing portal URL was not returned by the server');
        return;
      }
      window.open(portalUrl, '_blank', 'noopener,noreferrer');
      toast.success(
        billingPortalSendEmail
          ? 'Portal opened in a new tab. The customer may also receive the link by email.'
          : 'Billing portal opened in a new tab.'
      );
    } catch (error) {
      const n = getApiError(error);
      toast.error(n.detail || n.uiMessage || 'Could not create Stripe billing portal session');
    } finally {
      setBillingPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid md:grid-cols-2 gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  if (!agent) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-agent-detail">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/agents')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center">
            <Bot className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-jakarta font-bold text-slate-900">{agent.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>
                {agent.status}
              </Badge>
              <span className="text-slate-500 capitalize">• {agent.industry}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild>
            <Link to={`/admin/agents/${agent.agent_id}/configure`}>
              <Settings className="w-4 h-4 mr-2" />
              Configure
            </Link>
          </Button>
          {agent.customer_id && (
            <Button asChild variant="outline">
              <Link to={`/admin/customers/${agent.customer_id}/twilio?agentId=${encodeURIComponent(agent.agent_id)}`}>
                Twilio Config
              </Link>
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowEditModal(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="config">Configuration</TabsTrigger>
          <TabsTrigger value="calls">Call History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Agent Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 font-medium mb-1">Agent ID</p>
                  <p className="text-sm font-mono text-slate-800 break-all">{agent.agent_id}</p>
                </div>
                {agent.vapi_agent_id && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-600 font-medium mb-1">VAPI Agent ID</p>
                    <p className="text-sm font-mono text-blue-800 break-all">{agent.vapi_agent_id}</p>
                  </div>
                )}
                {agent.phone_number && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{agent.phone_number}</span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="capitalize">{agent.industry}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span>{agent.timezone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span>Created {formatDateTime(agent.created_at)}</span>
                </div>
                {agent.description && (
                  <div className="pt-2 border-t">
                    <p className="text-sm text-slate-600">{agent.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Subscription</CardTitle>
                <Button variant="outline" size="sm" onClick={() => setShowSubscriptionModal(true)}>
                  Change Plan
                </Button>
              </CardHeader>
              <CardContent>
                {agent.subscription ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Badge variant="default" className="text-base px-3 py-1">
                        {agent.subscription.plan_name}
                      </Badge>
                      <span className="text-2xl font-bold text-slate-900">
                        ${agent.subscription.price}/mo
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                      <div>
                        <p className="text-sm text-slate-500">Messages Remaining</p>
                        <p className="text-xl font-semibold text-slate-900">
                          {getRemainingMessageCount(agent) ?? 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Messages Used</p>
                        <p className="text-xl font-semibold text-slate-900">
                          {getUsedMessageCount(agent) ?? 0}
                        </p>
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-4 space-y-3">
                      <div>
                        <p className="text-sm font-medium text-slate-800">Stripe billing & cancellation</p>
                        <p className="text-xs text-slate-500 mt-1">
                          Opens the Stripe Customer Billing Portal for this agent&apos;s subscription so the customer
                          can cancel, update payment method, or view invoices. Requires a Stripe-backed subscription on
                          the server.
                        </p>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <Label htmlFor="billing-portal-send-email" className="text-sm font-normal text-slate-700">
                          Email portal link to customer
                        </Label>
                        <Switch
                          id="billing-portal-send-email"
                          checked={billingPortalSendEmail}
                          onCheckedChange={setBillingPortalSendEmail}
                          disabled={billingPortalLoading}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        className="w-full sm:w-auto"
                        onClick={handleStripeBillingPortal}
                        disabled={billingPortalLoading}
                      >
                        {billingPortalLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <ExternalLink className="w-4 h-4 mr-2" />
                        )}
                        Open Stripe billing portal
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Settings className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No subscription assigned</p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => setShowSubscriptionModal(true)}
                    >
                      Assign Plan
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {agent.customer && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Customer</CardTitle>
                </CardHeader>
                <CardContent>
                  <Link
                    to={`/admin/customers/${agent.customer_id}`}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-semibold">
                        {agent.customer.name?.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{agent.customer.name}</p>
                      <p className="text-sm text-slate-500">
                        {agent.customer.company_name || agent.customer.email}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="config" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Business Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {DAYS.map((day) => (
                    <div key={day} className="flex items-center justify-between py-2 border-b last:border-0">
                      <span className="capitalize font-medium">{day}</span>
                      {agent.business_hours?.[day]?.enabled ? (
                        <span className="text-slate-600">
                          {agent.business_hours[day].open} - {agent.business_hours[day].close}
                        </span>
                      ) : (
                        <span className="text-slate-400">Closed</span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Voice Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {agent.voice_config?.greeting_message && (
                  <div>
                    <Label className="text-slate-500">Greeting Message</Label>
                    <p className="text-slate-900 mt-1">{agent.voice_config.greeting_message}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Voice Provider</Label>
                    <p className="text-slate-900 mt-1 capitalize">
                      {agent.voice_config?.voice_provider || 'Not set'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Voice ID</Label>
                    <p className="text-slate-900 mt-1">
                      {agent.voice_config?.voice_id || 'Not set'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {agent.additional_details && (
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Additional Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 whitespace-pre-wrap">{agent.additional_details}</p>
                </CardContent>
              </Card>
            )}

            {(agent.prompts?.system_prompt || agent.prompts?.first_message) && (
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Custom Prompts</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {agent.prompts?.system_prompt && (
                    <div>
                      <Label className="text-slate-500">System Prompt</Label>
                      <p className="text-slate-900 mt-1 whitespace-pre-wrap">{agent.prompts.system_prompt}</p>
                    </div>
                  )}
                  {agent.prompts?.first_message && (
                    <div>
                      <Label className="text-slate-500">First Message</Label>
                      <p className="text-slate-900 mt-1">{agent.prompts.first_message}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="calls">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Calls</CardTitle>
            </CardHeader>
            <CardContent>
              {agent.recent_calls?.length === 0 ? (
                <div className="text-center py-12">
                  <PhoneCall className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No calls yet</h3>
                  <p className="text-slate-500">Call history will appear here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Minutes</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agent.recent_calls?.map((call) => (
                      <TableRow key={call.call_id}>
                        <TableCell>{formatDateTime(call.created_at)}</TableCell>
                        <TableCell>
                          {Math.floor(call.duration_seconds / 60)}:{String(call.duration_seconds % 60).padStart(2, '0')}
                        </TableCell>
                        <TableCell>{call.minutes_used}</TableCell>
                        <TableCell>
                          <Badge variant={call.status === 'completed' ? 'default' : 'secondary'}>
                            {call.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Agent</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {agent.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Modal */}
      <SubscriptionModal
        open={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        agentId={agentId}
        currentPlanId={agent.subscription?.plan_id}
        onSuccess={() => {
          setShowSubscriptionModal(false);
          loadAgent();
        }}
      />

      {/* Edit Agent Modal */}
      <EditAgentModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        agent={agent}
        onSuccess={() => {
          setShowEditModal(false);
          loadAgent();
        }}
      />
    </div>
  );
}

function SubscriptionModal({ open, onClose, agentId, currentPlanId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(currentPlanId || '');

  useEffect(() => {
    if (open) {
      loadPlans();
      setSelectedPlan(currentPlanId || '');
    }
  }, [open, currentPlanId]);

  const loadPlans = async () => {
    try {
      const response = await adminGetPlans();
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Failed to load plans');
    }
  };

  const handleSubmit = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }
    setLoading(true);
    try {
      await adminAssignAgentSubscription(agentId, selectedPlan);
      toast.success('Subscription updated');
      onSuccess();
    } catch (error) {
      toast.error('Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Change Subscription</DialogTitle>
          <DialogDescription>Select a plan for this agent</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-4">
          {plans.filter(p => p.is_active).map((plan) => (
            <div
              key={plan.plan_id}
              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                selectedPlan === plan.plan_id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => setSelectedPlan(plan.plan_id)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{plan.name}</h3>
                  <p className="text-sm text-slate-600">{plan.message_count ?? plan.minutes_per_month} messages/month</p>
                </div>
                <p className="text-xl font-bold text-slate-900">${plan.price}/mo</p>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Update Plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditAgentModal({ open, onClose, agent, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name || '',
        description: agent.description || '',
        phone_number: agent.phone_number || '',
        vapi_agent_id: agent.vapi_agent_id || '',
        industry: agent.industry || 'other',
        timezone: agent.timezone || 'Australia/Sydney',
        status: agent.status || 'draft',
        additional_details: agent.additional_details || '',
        greeting_message: agent.voice_config?.greeting_message || '',
        voice_provider: agent.voice_config?.voice_provider || 'elevenlabs',
        voice_id: agent.voice_config?.voice_id || '',
        system_prompt: agent.prompts?.system_prompt || '',
        first_message: agent.prompts?.first_message || '',
      });
    }
  }, [agent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminUpdateAgent(agent.agent_id, formData);
      toast.success('Agent updated successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Agent</DialogTitle>
          <DialogDescription>Update agent configuration</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="voice">Voice Settings</TabsTrigger>
              <TabsTrigger value="prompts">Prompts</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Agent Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                    placeholder="+61..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>VAPI Agent ID</Label>
                <Input
                  value={formData.vapi_agent_id}
                  onChange={(e) => setFormData({ ...formData, vapi_agent_id: e.target.value })}
                  placeholder="e.g., 550e8400-e29b-41d4-a716-446655440000"
                />
                <p className="text-sm text-slate-500">Link to existing VAPI agent</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Industry</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => setFormData({ ...formData, industry: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((ind) => (
                        <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Additional Details</Label>
                <Textarea
                  value={formData.additional_details}
                  onChange={(e) => setFormData({ ...formData, additional_details: e.target.value })}
                  rows={4}
                  placeholder="Business details, services, pricing, etc."
                />
              </div>
            </TabsContent>

            <TabsContent value="voice" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Greeting Message</Label>
                <Textarea
                  value={formData.greeting_message}
                  onChange={(e) => setFormData({ ...formData, greeting_message: e.target.value })}
                  rows={3}
                  placeholder="Hello! Thank you for calling..."
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Voice Provider</Label>
                  <Select
                    value={formData.voice_provider}
                    onValueChange={(value) => setFormData({ ...formData, voice_provider: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                      <SelectItem value="azure">Azure</SelectItem>
                      <SelectItem value="google">Google</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Voice ID</Label>
                  <Input
                    value={formData.voice_id}
                    onChange={(e) => setFormData({ ...formData, voice_id: e.target.value })}
                    placeholder="Voice ID from provider"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="prompts" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>System Prompt</Label>
                <Textarea
                  value={formData.system_prompt}
                  onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                  rows={5}
                  placeholder="You are a friendly assistant..."
                />
              </div>

              <div className="space-y-2">
                <Label>First Message</Label>
                <Textarea
                  value={formData.first_message}
                  onChange={(e) => setFormData({ ...formData, first_message: e.target.value })}
                  rows={2}
                  placeholder="The first thing the agent says..."
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

